import { useState } from "react";
import { toast } from "sonner";
import { LogOut, FileText, Sparkles, Mail, BarChart3, MailPlus, Linkedin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useSEO } from "@/hooks/useSEO";
import { LangSwitch } from "@/components/Navbar";
import LBLogo from "@/components/LBLogo";
import ArticlesPanel from "@/components/admin/ArticlesPanel";
import ArticleEditor from "@/components/admin/ArticleEditor";
import DraftsPanel from "@/components/admin/DraftsPanel";
import MessagesPanel from "@/components/admin/MessagesPanel";
import StatsPanel from "@/components/admin/StatsPanel";
import NewsletterPanel from "@/components/admin/NewsletterPanel";
import LinkedInPanel from "@/components/admin/LinkedInPanel";

function AdminLogin() {
  const { tr } = useLang();
  const a = tr("admin");
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      toast.success(a.loginTitle);
    } catch {
      setError(a.loginError);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none";

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-6 noise" data-testid="admin-login-page">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-gold/20 bg-navy-light p-10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)]"
      >
        <div className="flex justify-center">
          <LBLogo size={56} />
        </div>
        <h1 className="mt-8 text-center font-serif text-3xl text-ivory" data-testid="admin-login-title">
          {a.loginTitle}
        </h1>
        <p className="mt-3 text-center text-sm text-ivory/50">{a.loginSub}</p>
        <div className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
              {a.email}
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              autoComplete="username"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
              {a.password}
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>
        {error && (
          <p className="mt-5 border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-8 w-full bg-gold px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-bright disabled:pointer-events-none disabled:opacity-50"
        >
          {busy ? a.loggingIn : a.login}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  useSEO("admin");
  const { lang, tr } = useLang();
  const a = tr("admin");
  const { user, ready, logout } = useAdminAuth();
  const [tab, setTab] = useState("articles");
  const [editing, setEditing] = useState(undefined);
  const [draftsCount, setDraftsCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>
      </div>
    );
  }

  if (!user) return <AdminLogin />;

  const tabs = [
    { id: "articles", label: a.tabs.articles, icon: FileText, count: 0 },
    { id: "drafts", label: a.tabs.drafts, icon: Sparkles, count: draftsCount },
    { id: "messages", label: a.tabs.messages, icon: Mail, count: unreadCount },
    { id: "newsletter", label: a.tabs.newsletter, icon: MailPlus, count: 0 },
    { id: "linkedin", label: a.tabs.linkedin, icon: Linkedin, count: 0 },
    { id: "stats", label: a.tabs.stats, icon: BarChart3, count: 0 },
  ];

  return (
    <div className="min-h-screen bg-navy text-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-navy/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-4">
            <LBLogo size={40} />
            <div>
              <p className="font-serif text-lg leading-tight text-ivory">LB Trade Advisory</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Admin · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LangSwitch compact />
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-ivory/15 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-ivory/60 transition-colors hover:border-red-400/50 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> {a.logout}
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 px-6 lg:px-8" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => {
                setTab(t.id);
                setEditing(undefined);
              }}
              className={`flex items-center gap-2 border-b-2 px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                tab === t.id ? "border-gold text-gold-bright" : "border-transparent text-ivory/45 hover:text-ivory/80"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              {t.count > 0 && (
                <span className="bg-gold px-1.5 py-0.5 text-[10px] font-semibold text-navy">{t.count}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
        {tab === "articles" &&
          (editing !== undefined ? (
            <ArticleEditor
              key={editing?.id || "new"}
              article={editing}
              onCancel={() => setEditing(undefined)}
              onSaved={() => setEditing(undefined)}
            />
          ) : (
            <ArticlesPanel onEdit={setEditing} />
          ))}
        {tab === "drafts" && (
          <DraftsPanel
            onCount={setDraftsCount}
            onAccepted={(article) => {
              setTab("articles");
              setEditing(article);
            }}
          />
        )}
        {tab === "messages" && <MessagesPanel onCount={setUnreadCount} />}
        {tab === "newsletter" && <NewsletterPanel />}
        {tab === "linkedin" && <LinkedInPanel />}
        {tab === "stats" && <StatsPanel />}
      </main>
    </div>
  );
}
